import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

const API_BASE = 'https://guardiancloudcare.com/backend/api';

type SupervisorCase = {
  case_intake_id: number;
  case_number: string;
  case_status: string;
  priority_level: string | null;
  progress_note: string | null;
  created_at: string;
  report_uid: string;
  assigned_social_worker_uid: string | null;
  incident_type: string | null;
  urgency: string | null;
  location: string | null;
  barangay: string | null;
  municipality: string | null;
  province: string | null;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  age: number | null;
  gender: string | null;
  assigned_social_worker_name: string | null;
  child_full_name: string;
  assignment_status: string;
};

type WorkerWorkload = {
  worker_uid: string;
  worker_name: string;
  active_cases: number;
  completed_cases: number;
  total_assigned_cases: number;
  workload_level: string;
};

type RecentActivity = {
  id: string;
  caseNumber: string;
  type: string;
  title: string;
  description: string;
  date: string | null;
  actorUid: string | null;
  actorName: string;
  caseStatus: string | null;
  priorityLevel: string | null;
};

type DashboardData = {
  cases: SupervisorCase[];
  workers: WorkerWorkload[];
  officeName: string;
};

const normalize = (value: string | null | undefined) =>
  (value ?? '').trim().toLowerCase();

const isTerminalStatus = (status: string | null | undefined) =>
  ['closed', 'completed', 'cancelled', 'canceled', 'archived'].includes(
    normalize(status)
  );

const isPendingApproval = (status: string | null | undefined) =>
  ['for approval', 'pending approval'].includes(normalize(status));

const isCriticalCase = (item: SupervisorCase) => {
  const priority = normalize(item.priority_level);
  const urgency = normalize(item.urgency);

  return (
    priority === 'critical' ||
    urgency === 'critical' ||
    urgency === 'urgent'
  );
};

const isClosedThisMonth = (item: SupervisorCase) => {
  if (!['closed', 'completed'].includes(normalize(item.case_status))) {
    return false;
  }

  // Uses created_at because the current case API does not provide
  // a separate case-closure timestamp.
  const date = new Date(item.created_at);
  const now = new Date();

  return (
    !Number.isNaN(date.getTime()) &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
};

const getIncidentColor = (incidentType: string | null) => {
  switch (normalize(incidentType)) {
    case 'physical abuse':
      return 'bg-red-500';
    case 'neglect':
      return 'bg-amber-500';
    case 'sexual abuse':
      return 'bg-rose-500';
    case 'child labor':
      return 'bg-orange-500';
    default:
      return 'bg-teal-500';
  }
};

export default function SupervisorDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const supervisorUid = user?.uid;

  const [dashboard, setDashboard] = useState<DashboardData>({
    cases: [],
    workers: [],
    officeName: '',
  });

  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [activityError, setActivityError] = useState('');
  const [lastSync, setLastSync] = useState('');

  const loadDashboard = useCallback(
    async (isRefresh = false) => {
      if (!supervisorUid) {
        setLoading(false);
        setError('Supervisor account information is unavailable.');
        return;
      }

      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');

      try {
        const query = `supervisor_uid=${encodeURIComponent(supervisorUid)}`;

        const [casesResponse, workloadResponse, activityResponse] =
          await Promise.all([
            fetch(
              `${API_BASE}/get_supervisor_case_assignments.php?${query}`,
              { headers: { Accept: 'application/json' } }
            ),
            fetch(
              `${API_BASE}/get_supervisor_worker_workloads.php?${query}`,
              { headers: { Accept: 'application/json' } }
            ),
            fetch(
              `${API_BASE}/get_supervisor_recent_activity.php?${query}&limit=10`,
              { headers: { Accept: 'application/json' } }
            ),
          ]);

        if (!casesResponse.ok || !workloadResponse.ok) {
          throw new Error(
            'Unable to retrieve dashboard information from the server.'
          );
        }

        const casesJson = await casesResponse.json();
        const workloadJson = await workloadResponse.json();

        if (!casesJson.success) {
          throw new Error(
            casesJson.message || 'Unable to load supervisor cases.'
          );
        }

        if (!workloadJson.success) {
          throw new Error(
            workloadJson.message || 'Unable to load worker workloads.'
          );
        }

        setDashboard({
          cases: Array.isArray(casesJson.data) ? casesJson.data : [],
          workers: Array.isArray(workloadJson.workers)
            ? workloadJson.workers
            : [],
          officeName:
            casesJson.office_name || workloadJson.office_name || '',
        });

        // Recent Activity is independent of the other dashboard sections.
        // If its endpoint fails, cases and workloads can still load.
        try {
          if (!activityResponse.ok) {
            throw new Error('Unable to load recent activity.');
          }

          const activityJson = await activityResponse.json();

          if (!activityJson.success) {
            throw new Error(
              activityJson.message || 'Unable to load recent activity.'
            );
          }

          const activities = activityJson.data?.activities;

          setRecentActivities(
            Array.isArray(activities) ? activities : []
          );
          setActivityError('');
        } catch (activityErr) {
          console.error('Recent activity error:', activityErr);
          setActivityError(
            activityErr instanceof Error
              ? activityErr.message
              : 'Unable to load recent activity.'
          );
        }

        setLastSync(new Date().toLocaleTimeString());
      } catch (err) {
        console.error('Supervisor dashboard error:', err);

        setError(
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred while loading the dashboard.'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [supervisorUid]
  );

  useEffect(() => {
    if (!supervisorUid) {
      setLoading(false);
      setError('Supervisor account information is unavailable.');
      return;
    }

    void loadDashboard();

    const interval = window.setInterval(() => {
      void loadDashboard(true);
    }, 30000);

    return () => window.clearInterval(interval);
  }, [supervisorUid, loadDashboard]);

  const cases = dashboard.cases;
  const totalCases = cases.length;

  const activeCases = cases.filter(
    (item) => !isTerminalStatus(item.case_status)
  ).length;

  const pendingCases = cases.filter((item) =>
    isPendingApproval(item.case_status)
  );

  const criticalCases = cases.filter(isCriticalCase);

  const closedThisMonth = cases.filter(isClosedThisMonth).length;

  const municipalityCounts = cases.reduce<Record<string, number>>(
    (counts, item) => {
      const municipality =
        item.municipality?.trim() || 'Unspecified municipality';

      counts[municipality] = (counts[municipality] || 0) + 1;
      return counts;
    },
    {}
  );

  const municipalityEntries = Object.entries(municipalityCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const maxMunicipalityCount = Math.max(
    1,
    ...municipalityEntries.map(([, count]) => count)
  );

  const incidentCounts = cases.reduce<Record<string, number>>(
    (counts, item) => {
      const incident =
        item.incident_type?.trim() || 'Unspecified incident';

      counts[incident] = (counts[incident] || 0) + 1;
      return counts;
    },
    {}
  );

  const incidentEntries = Object.entries(incidentCounts).sort(
    (a, b) => b[1] - a[1]
  );

  const maxIncidentCount = Math.max(
    1,
    ...incidentEntries.map(([, count]) => count)
  );

  const dashboardCards = [
    {
      label: 'Total Cases',
      value: totalCases,
      icon: 'ri-folder-open-line',
      color: 'text-gray-700',
      bg: 'bg-gray-50',
      path: '/sup-oversight',
    },
    {
      label: 'Active Cases',
      value: activeCases,
      icon: 'ri-folder-user-line',
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      path: '/sup-oversight',
    },
    {
      label: 'Critical Alerts',
      value: criticalCases.length,
      icon: 'ri-alarm-warning-line',
      color: 'text-red-600',
      bg: 'bg-red-50',
      path: '/sup-escalation',
    },
    {
      label: 'Pending Approvals',
      value: pendingCases.length,
      icon: 'ri-time-line',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      path: '/sup-approvals',
    },
    {
      label: 'Closed This Month',
      value: closedThisMonth,
      icon: 'ri-checkbox-circle-line',
      color: 'text-teal-600',
      bg: 'bg-teal-50',
      path: '/sup-oversight',
    },
  ];

  return (
    <AppLayout
      title="Supervisor Dashboard"
      breadcrumb={['Supervisor', 'Dashboard']}
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-700">
          <span
            className={`h-2 w-2 rounded-full ${
              error ? 'bg-amber-400' : 'animate-pulse bg-teal-400'
            }`}
          />
          {error ? 'Sync issue' : 'Live data'}
          {lastSync && ` · Last sync: ${lastSync}`}
        </div>

        <button
          type="button"
          onClick={() => void loadDashboard(true)}
          disabled={refreshing || loading}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-teal-300 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {refreshing ? 'Refreshing...' : 'Refresh data'}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {error}
          <p className="mt-1 text-xs">
            Previously loaded data is retained if a refresh fails.
          </p>
        </div>
      )}

      {dashboard.officeName && (
        <p className="mb-5 text-sm text-gray-500">
          Office:{' '}
          <strong className="text-gray-800">
            {dashboard.officeName}
          </strong>
        </p>
      )}

      <div className="space-y-6">
        {/* Dashboard statistics */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
          {dashboardCards.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => navigate(item.path)}
              className="cursor-pointer rounded-xl border border-gray-100 bg-white p-5 text-left transition-all hover:border-teal-200"
            >
              <div
                className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${item.bg}`}
              >
                <i className={`${item.icon} ${item.color} text-lg`} />
              </div>

              <p className="text-3xl font-bold text-gray-900">
                {loading ? '—' : item.value}
              </p>

              <p className="mt-1 text-sm text-gray-500">{item.label}</p>
            </button>
          ))}
        </div>

        {/* Municipality and incident insights */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* <div className="rounded-xl border border-gray-100 bg-white p-5">
            <h3 className="mb-1 font-semibold text-gray-800">
              Cases by Municipality
            </h3>

            <p className="mb-4 text-xs text-gray-500">
              Distribution of cases by municipality in your assigned office
            </p>

            {loading ? (
              <p className="py-5 text-sm text-gray-500">
                Loading municipality data...
              </p>
            ) : municipalityEntries.length === 0 ? (
              <p className="py-5 text-sm text-gray-500">
                No municipality data available.
              </p>
            ) : (
              municipalityEntries.map(([municipality, count]) => {
                const percentage =
                  totalCases > 0
                    ? Math.round((count / totalCases) * 100)
                    : 0;

                return (
                  <div key={municipality} className="mb-4">
                    <div className="mb-1 flex justify-between gap-3 text-sm">
                      <span className="text-gray-700">{municipality}</span>
                      <span className="font-bold text-gray-900">
                        {count} ({percentage}%)
                      </span>
                    </div>

                    <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-teal-500"
                        style={{
                          width: `${
                            (count / maxMunicipalityCount) * 100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div> */}

          <div className="rounded-xl border border-gray-100 bg-white p-5">
            <h3 className="mb-1 font-semibold text-gray-800">
              Incident Type Insights
            </h3>

            <p className="mb-4 text-xs text-gray-500">
              Distribution of reported incidents
            </p>

            {loading ? (
              <p className="py-5 text-sm text-gray-500">
                Loading incident data...
              </p>
            ) : incidentEntries.length === 0 ? (
              <p className="py-5 text-sm text-gray-500">
                No incident data available.
              </p>
            ) : (
              <div className="space-y-4">
                {incidentEntries.map(([incident, count]) => (
                  <div key={incident}>
                    <div className="mb-1 flex justify-between gap-3 text-sm">
                      <span className="text-gray-700">{incident}</span>
                      <span className="font-bold text-gray-900">
                        {count} {count === 1 ? 'case' : 'cases'}
                      </span>
                    </div>

                    <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className={`h-full rounded-full ${getIncidentColor(
                          incident
                        )}`}
                        style={{
                          width: `${
                            (count / maxIncidentCount) * 100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs text-amber-700">
                <i className="ri-information-line mr-1" />
                These counts describe currently returned cases. Historical
                trends require dated records for a comparison period.
              </p>
            </div>
          </div>
        </div>

        {/* High-risk alerts */}
        <div className="overflow-hidden rounded-xl border border-red-200 bg-white">
          <div className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-5 py-4">
            <i className="ri-alarm-warning-line text-red-500" />
            <h3 className="font-semibold text-red-800">
              High-Risk Case Alerts
            </h3>

            <span className="ml-auto rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
              {criticalCases.length}
            </span>
          </div>

          <div className="divide-y divide-gray-50">
            {criticalCases.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-500">
                {loading
                  ? 'Loading alerts...'
                  : 'No critical or urgent cases found in the returned data.'}
              </p>
            ) : (
              criticalCases.map((item) => (
                <div
                  key={item.case_number}
                  className="flex items-center gap-4 px-5 py-4"
                >
                  <div className="h-2 w-2 flex-shrink-0 animate-pulse rounded-full bg-red-500" />

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-800">
                      {item.child_full_name || 'Name unavailable'} —{' '}
                      <span className="font-mono text-xs text-gray-500">
                        {item.case_number}
                      </span>
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      {item.incident_type || 'Incident unspecified'} ·{' '}
                      {item.barangay ||
                        item.location ||
                        'Location unavailable'}{' '}
                      · {item.assigned_social_worker_name || 'Unassigned'}
                    </p>

                    <p className="mt-1 text-xs font-medium uppercase text-red-600">
                      {item.urgency || item.priority_level || 'Critical'}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (item.report_uid) {
                        navigate(
                          `/sup-victim-reports/${encodeURIComponent(item.report_uid)}`
                        );
                      } else {
                        alert('The victim report ID is unavailable for this case.');
                      }
                    }}
                    className="whitespace-nowrap rounded-lg bg-red-100 px-3 py-1.5 text-xs text-red-700 hover:bg-red-200"
                  >
                    View Case
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Live Recent Activity */}
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <div>
              <h3 className="font-semibold text-gray-800">
                Recent Activity
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Latest timeline updates and case reassignments
              </p>
            </div>

            <button
              type="button"
              onClick={() => void loadDashboard(true)}
              disabled={refreshing || loading}
              className="whitespace-nowrap text-xs text-teal-600 hover:text-teal-700 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>

          {activityError && (
            <div
              role="alert"
              className="border-b border-amber-100 bg-amber-50 px-5 py-3 text-xs text-amber-700"
            >
              {activityError}
            </div>
          )}

          <div className="divide-y divide-gray-50">
            {loading && recentActivities.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-500">
                Loading recent activity...
              </p>
            ) : recentActivities.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-500">
                No activity records found.
              </p>
            ) : (
              recentActivities.map((activity) => {
                const isReassignment = activity.type === 'reassignment';

                const formattedDate = activity.date
                  ? (() => {
                      const parsedDate = new Date(
                        activity.date.replace(' ', 'T')
                      );

                      return Number.isNaN(parsedDate.getTime())
                        ? activity.date
                        : parsedDate.toLocaleString();
                    })()
                  : 'Date unavailable';

                const isHighPriority = ['urgent', 'critical'].includes(
                  (activity.priorityLevel ?? '').toLowerCase()
                );

                return (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 px-5 py-4"
                  >
                    <div
                      className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${
                        isReassignment ? 'bg-blue-50' : 'bg-teal-50'
                      }`}
                    >
                      <i
                        className={`text-sm ${
                          isReassignment
                            ? 'ri-user-shared-line text-blue-600'
                            : 'ri-history-line text-teal-600'
                        }`}
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800">
                        {activity.title}
                      </p>

                      <p className="mt-1 text-sm text-gray-600">
                        {activity.description || 'No additional details.'}
                      </p>

                      <p className="mt-2 text-xs text-gray-500">
                        {activity.actorName || 'System'} ·{' '}
                        {activity.caseNumber}
                      </p>

                      <p className="mt-1 text-xs text-gray-400">
                        {formattedDate}
                      </p>

                      {activity.caseStatus && (
                        <span className="mt-2 inline-block rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                          {activity.caseStatus}
                        </span>
                      )}
                    </div>

                    {isHighPriority && (
                      <span className="whitespace-nowrap rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                        {activity.priorityLevel}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Worker workload summary */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <h3 className="font-semibold text-gray-800">
            Office Workload Summary
          </h3>

          <p className="mb-3 mt-1 text-xs text-gray-500">
            Current workload from approved Social Workers in your office
          </p>

          {dashboard.workers.length === 0 ? (
            <p className="text-sm text-gray-500">
              {loading
                ? 'Loading worker workloads...'
                : 'No worker workload records available.'}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {dashboard.workers.map((worker) => (
                <div
                  key={worker.worker_uid}
                  className="rounded-lg border border-gray-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {worker.worker_name}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {worker.worker_uid}
                      </p>
                    </div>

                    <span className="rounded-full bg-teal-50 px-2 py-1 text-xs text-teal-700">
                      {worker.workload_level}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm">
                    <span>
                      Active: <strong>{worker.active_cases}</strong>
                    </span>
                    <span>
                      Completed: <strong>{worker.completed_cases}</strong>
                    </span>
                    <span>
                      Total assigned:{' '}
                      <strong>{worker.total_assigned_cases}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-sm font-semibold text-blue-800">
            Supervisor oversight
          </p>
          <p className="mt-1 text-xs text-blue-700">
            Dashboard case counts, worker workloads, and recent activity are
            retrieved from the backend and refreshed automatically.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}